import { IQuartzJobEntry } from "../../../@types/admin/AdminQuartzJobs";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ModalContainer,
  ModalHeader,
  ModalJsonContainer,
  ModalLabel,
  ModalSubtitle,
  ModalTitle,
  ModalValue,
  StatusBadge,
  WarningNotice,
} from "../shared";
import {
  DetailGrid,
  DetailSectionTitle,
  WardList,
  WardListItem,
  WarningList,
} from "./AdminQuartzJobs.styles";
import {
  relativeTime,
  scheduleLabel,
  stateLabel,
  stateTone,
} from "./quartzJobsPresentation";

interface IQuartzJobDetailModalProps {
  entry: IQuartzJobEntry;
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
}) {
  let shown: string;
  if (value === null || value === undefined || value === "") shown = "—";
  else if (typeof value === "boolean") shown = value ? "Yes" : "No";
  else shown = String(value);

  return (
    <div>
      <ModalLabel>{label}</ModalLabel>
      <ModalValue>{shown}</ModalValue>
    </div>
  );
}

function when(iso: string | null | undefined): string {
  if (!iso) return "";
  return `${getLongFormattedDate(iso)} · ${relativeTime(iso)}`;
}

/**
 * Everything the API knows about one scheduler entry: the Quartz job and
 * trigger as stored, the schedule, the fire times, the workflow / wards /
 * hospital / tenant it serves, the warnings, and the raw job data.
 */
export function QuartzJobDetailModal({ entry }: IQuartzJobDetailModalProps) {
  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>{entry.jobName}</ModalTitle>
        <ModalSubtitle>
          {entry.category} · {entry.jobGroup}
          {entry.triggerName ? ` · ${entry.triggerName}` : " · no trigger"}
        </ModalSubtitle>
      </ModalHeader>

      <div>
        <StatusBadge status={stateTone(entry)}>{stateLabel(entry)}</StatusBadge>
      </div>

      {entry.warnings.length > 0 ? (
        <WarningNotice as="div">
          <WarningList>
            {entry.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </WarningList>
        </WarningNotice>
      ) : null}

      <DetailSectionTitle>Schedule</DetailSectionTitle>
      <DetailGrid>
        <Field label="Schedule" value={scheduleLabel(entry)} />
        <Field label="Cron expression" value={entry.cronExpression} />
        <Field label="Time zone" value={entry.timeZoneId} />
        <Field label="Trigger type" value={entry.triggerType} />
        <Field label="Next fire" value={when(entry.nextFireTimeUtc)} />
        <Field label="Last fire" value={when(entry.previousFireTimeUtc)} />
        <Field label="Start" value={when(entry.startTimeUtc)} />
        <Field label="End" value={when(entry.endTimeUtc)} />
        <Field label="Misfire policy" value={entry.misfireInstruction} />
        <Field label="Priority" value={entry.priority} />
        {entry.isExecuting ? (
          <Field
            label="Running since"
            value={`${when(entry.executingSinceUtc)} (${
              entry.executingRunTime ?? ""
            })`}
          />
        ) : null}
      </DetailGrid>

      <DetailSectionTitle>Job</DetailSectionTitle>
      <DetailGrid>
        <Field label="Job type" value={entry.jobType} />
        <Field label="Region" value={entry.region} />
        <Field label="Durable" value={entry.durable} />
        <Field
          label="Concurrent execution disallowed"
          value={entry.concurrentExecutionDisallowed}
        />
        <Field label="Requests recovery" value={entry.requestsRecovery} />
        {entry.executionEnabled !== undefined &&
        entry.executionEnabled !== null ? (
          <Field
            label={`Enabled by config (${
              entry.executionEnabledConfigKey ?? ""
            })`}
            value={entry.executionEnabled}
          />
        ) : null}
        {entry.jobDescription ? (
          <Field label="Description" value={entry.jobDescription} />
        ) : null}
      </DetailGrid>

      {entry.workflow ? (
        <>
          <DetailSectionTitle>Workflow</DetailSectionTitle>
          <DetailGrid>
            <Field label="Workflow id" value={entry.workflow.workflowId} />
            <Field
              label="Title"
              value={
                entry.workflow.found
                  ? entry.workflow.title
                  : "Not found in this region"
              }
            />
            <Field label="Published" value={entry.workflow.published} />
            <Field
              label="First step rule type"
              value={entry.workflow.firstStepRuleType}
            />
            <Field label="Trigger rule id" value={entry.workflow.ruleId} />
            <Field
              label="Current first step id"
              value={entry.workflow.currentFirstStepId}
            />
          </DetailGrid>
        </>
      ) : null}

      {entry.wards.length > 0 ? (
        <>
          <DetailSectionTitle>Wards</DetailSectionTitle>
          <WardList>
            {entry.wards.map((ward) => (
              <WardListItem key={ward.wardId}>
                <strong>
                  {ward.found
                    ? ward.wardName ?? `Ward ${ward.wardId}`
                    : `Ward ${ward.wardId} (not found)`}
                </strong>
                <span>
                  {[
                    ward.hospitalGroupName,
                    ward.tenantName,
                    ward.hospitalGroupTimezone,
                  ]
                    .filter(Boolean)
                    .join(" · ") || `id ${ward.wardId}`}
                </span>
                {ward.integrationEnabled !== undefined &&
                ward.integrationEnabled !== null ? (
                  <StatusBadge
                    status={ward.integrationEnabled ? "active" : "error"}
                  >
                    {ward.integrationEnabled
                      ? "Integration enabled"
                      : "Integration disabled"}
                  </StatusBadge>
                ) : null}
              </WardListItem>
            ))}
          </WardList>
        </>
      ) : null}

      <DetailSectionTitle>Job data</DetailSectionTitle>
      <ModalJsonContainer>
        {JSON.stringify(entry.jobData, null, 2)}
      </ModalJsonContainer>
    </ModalContainer>
  );
}
